import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { gql, useLazyQuery } from "@apollo/client";
import { createAndStorePkce, PKCE_VERIFIER_KEY, OAUTH_STATE_KEY } from "../utils/pkce";

const CHECK_USER = gql`query CheckUser { checkUser }`;
const clean = (v) => (v || "").replace(/^https?:\/\//, "").replace(/\/+$/, "");
const DEFAULT_COGNITO_HOST = "pounce-dev-colabo.auth.us-east-1.amazoncognito.com";

const RAW_COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
let COGNITO_HOST = clean(RAW_COGNITO_DOMAIN);
if (!COGNITO_HOST || COGNITO_HOST.startsWith(".") || COGNITO_HOST.includes("..")) {
  console.warn("[Cognito] Bad/empty domain from env:", RAW_COGNITO_DOMAIN, "→ using DEFAULT");
  COGNITO_HOST = DEFAULT_COGNITO_HOST;
}

const REDIRECT_URI = `${window.location.origin}/callback`;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";

const COGNITO_SCOPES = "openid email profile";

function decodeJwt(token) {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = "===".slice((4 - (base.length % 4)) % 4);
    return JSON.parse(atob(base + pad));
  } catch {
    return null;
  }
}

function isJwtValid(token, skewSeconds = 60) {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== "number") return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now + skewSeconds;
}

function bestBearer() {
  const id = localStorage.getItem("idToken");
  if (isJwtValid(id)) return id;
  const access = localStorage.getItem("accessToken");
  if (isJwtValid(access)) return access;
  return null;
}

const Callback = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const exchangeStartedRef = useRef(false);

  const [checkUser, { called, data, error, loading }] = useLazyQuery(CHECK_USER, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const qs = new URLSearchParams(search);
    const code = qs.get("code");
    const returnedState = qs.get("state");
    const cognitoError = qs.get("error");
    const errorDesc = qs.get("error_description");

    console.log("[Callback]", { code, cognitoError, errorDesc });

    if (cognitoError) {
      console.error("[Callback] Cognito error:", cognitoError, errorDesc);
      navigate("/login");
      return;
    }

    if (!code) {
      console.error("No authorization code in URL. Sending user to /login.");
      navigate("/login");
      return;
    }

    const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
    if (expectedState && returnedState && expectedState !== returnedState) {
      console.error("[Callback] State mismatch");
      sessionStorage.removeItem(OAUTH_STATE_KEY);
      navigate("/login");
      return;
    }
    sessionStorage.removeItem(OAUTH_STATE_KEY);

    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    const existingBearer = bestBearer();
    if (existingBearer) {
      setIsAuthenticated(true);
      checkUser({ context: { headers: { Authorization: `Bearer ${existingBearer}` } } });
      try { window.history.replaceState({}, document.title, REDIRECT_URI); } catch {}
      exchangeStartedRef.current = false;
      return;
    }

    const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!codeVerifier) {
      console.error("[Callback] Missing PKCE code_verifier; restarting login with new PKCE.");
      (async () => {
        try {
          const { codeChallenge, state } = await createAndStorePkce();
          const lastIdp = sessionStorage.getItem("lastIdp") || "";
          const authParams = new URLSearchParams({
            redirect_uri: REDIRECT_URI,
            response_type: "code",
            client_id: COGNITO_CLIENT_ID,
            scope: COGNITO_SCOPES,
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
            state,
            prompt: "login select_account",
            ...(lastIdp ? { identity_provider: lastIdp } : {}),
          });
          window.location.assign(
            `https://${COGNITO_HOST}/oauth2/authorize?${authParams.toString()}`
          );
        } catch (e) {
          console.error("[Callback] Failed to restart with PKCE:", e);
          navigate("/login");
        } finally {
          exchangeStartedRef.current = false;
        }
      })();
      return;
    }

    (async () => {
      try {
        const body = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: COGNITO_CLIENT_ID,
          code,
          redirect_uri: REDIRECT_URI,
          code_verifier: codeVerifier,
        });

        const res = await fetch(`https://${COGNITO_HOST}/oauth2/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body,
        });

        const tokenData = await res.json();
        if (!res.ok || tokenData.error) {
          throw new Error(tokenData.error_description || `HTTP ${res.status}`);
        }

        const { id_token, access_token, refresh_token } = tokenData;
        if (!id_token) throw new Error("No ID token returned from Cognito");

        localStorage.setItem("idToken", id_token);
        if (access_token) localStorage.setItem("accessToken", access_token);
        if (refresh_token) localStorage.setItem("refreshToken", refresh_token);
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);

        setIsAuthenticated(true);
        await checkUser({ context: { headers: { Authorization: `Bearer ${id_token}` } } });
      } catch (err) {
        console.error("Token exchange failed:", err);
        navigate("/login");
      } finally {
        exchangeStartedRef.current = false;
        try { window.history.replaceState({}, document.title, REDIRECT_URI); } catch {}
      }
    })();
  }, [search, setIsAuthenticated, navigate, checkUser]);

  useEffect(() => {
    if (!called || loading) return;
    if (error) {
      navigate("/signupp", { replace: true, state: { fromSSO: true } });
      return;
    }
    if (data?.checkUser === true) navigate("/home", { replace: true });
    else navigate("/signupp", { replace: true, state: { fromSSO: true } });
  }, [called, data, error, loading, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
      <p className="text-lg">Signing you in…</p>
    </div>
  );
};

export default Callback;
